/**
 * @description Utility function to check network connectivity by making a simple request.
 * This helps determine if the network is actually available before attempting token refresh.
 *
 * @param {number} timeout - Timeout in milliseconds for the connectivity check (default: 5000).
 * @returns {Promise<boolean>} - Returns true if network is available, otherwise false.
 */
export async function checkNetworkConnectivity(
  timeout = 5000
): Promise<boolean> {
  try {
    // Use a simple HEAD request to a reliable endpoint
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch('/api/healthcheck', {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-cache',
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    // Network is not available or request failed
    return false;
  }
}

/**
 * @description Wait for network connectivity to be restored.
 * Polls network status until connection is available or max attempts reached.
 *
 * @param {Object} options - Configuration options.
 * @param {number} options.maxAttempts - Maximum number of check attempts (default: 10).
 * @param {number} options.checkInterval - Interval between checks in milliseconds (default: 2000).
 * @returns {Promise<boolean>} - Returns true if network is restored, otherwise false.
 */
export async function waitForNetworkRestoration(
  options: {
    maxAttempts?: number;
    checkInterval?: number;
  } = {}
): Promise<boolean> {
  const { maxAttempts = 10, checkInterval = 2000 } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    // Check browser online status first
    if (navigator?.onLine === false) {
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
      continue;
    }

    // Check actual network connectivity
    const isConnected = await checkNetworkConnectivity(3000);
    if (isConnected) {
      return true;
    }

    // Wait before next attempt
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
  }

  return false;
}
